import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../common/AnimatedSection";

const LeadershipMessage = ({ data = [] }) => {
  const [active_index, set_active_index] = useState(0);
  const [is_paused, set_is_paused] = useState(false);

  // ensure data is always safe
  const safe_data = Array.isArray(data) ? data : [];

  // reset index if data changes
  useEffect(() => {
    set_active_index(0);
  }, [safe_data]);

  // auto slider logic with hover pause
  useEffect(() => {
    if (safe_data.length === 0 || is_paused) return;

    const interval = setInterval(() => {
      set_active_index((prev) =>
        prev === safe_data.length - 1 ? 0 : prev + 1
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [safe_data, is_paused]);

  // prevent crash before data loads
  if (safe_data.length === 0) return null;

  const current = safe_data[active_index];

  return (
    <section className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary/[0.06] text-primary text-xs font-bold rounded-full tracking-widest uppercase">
              leadership
            </span>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.1}>
          <div 
            className="relative"
            onMouseEnter={() => set_is_paused(true)}
            onMouseLeave={() => set_is_paused(false)}
          >

            <div className="relative bg-white rounded-3xl shadow-2xl shadow-dark/[0.06] border border-dark/[0.04] overflow-hidden">

              {/* progress bar - resets/pauses on hover */}
              <motion.div
                key={`bar-${active_index}-${is_paused}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ 
                  duration: is_paused ? 0 : 6, 
                  ease: "linear" 
                }}
                className={`absolute top-0 left-0 right-0 h-1.5 origin-left z-10 ${
                  is_paused ? "bg-primary/20" : "bg-gradient-to-r from-primary via-primary/80 to-primary/40"
                }`}
              />

              <AnimatePresence mode="wait">
                <motion.div
                  key={active_index}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.6 }}
                  className="grid lg:grid-cols-5"
                >

                  {/* profile */}
                  <div className="lg:col-span-2 p-10 md:p-14 flex flex-col items-center justify-center bg-gradient-to-br from-primary/[0.03]">
                    <div className="w-44 h-44 rounded-full bg-primary/[0.08] flex items-center justify-center ring-[6px] ring-white shadow-xl overflow-hidden">
                      <img
                        src={current?.image || "/placeholder.png"}
                        alt={current?.name || "leader"}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="text-center mt-8">
                      <h3 className="text-xl font-bold text-dark">
                        {current?.name}
                      </h3>
                      <p className="text-sm text-primary font-semibold mt-1">
                        {current?.designation}
                      </p>
                      <p className="text-xs text-dark/30 mt-2">
                        {current?.years_experience}+ years experience
                      </p>
                    </div>
                  </div>

                  {/* content */}
                  <div className="lg:col-span-3 p-10 md:p-14 lg:p-16 flex flex-col justify-center">
                    <h2 className="text-sm font-bold text-primary tracking-widest uppercase mb-6">
                      message from leadership
                    </h2>

                    <blockquote className="text-xl md:text-2xl text-dark/75 font-light italic leading-relaxed">
                      {current?.message_title}
                    </blockquote>

                    <p className="mt-8 text-dark/60 leading-relaxed">
                      {current?.message}
                    </p>

                    {/* stats */}
                    <div className="mt-10 flex flex-wrap gap-6 pt-8 border-t border-dark/10">
                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.years_experience || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          years experience
                        </div>
                      </div>

                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.lives_affected || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          lives impacted
                        </div>
                      </div>

                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.partnerships || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          partnerships
                        </div>
                      </div>
                    </div>
                  </div>

                </motion.div>
              </AnimatePresence>

            </div>

            {/* dots */}
            <div className="flex justify-center gap-2 mt-8">
              {safe_data.map((_, i) => (
                <button
                  key={i}
                  onClick={() => set_active_index(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    active_index === i
                      ? "w-8 bg-primary"
                      : "w-2 bg-dark/10"
                  }`}
                />
              ))}
            </div>

          </div>
        </AnimatedSection>

      </div>
    </section>
  );
};

export default LeadershipMessage;